/** 動的グループ・権限セットの問い合わせ(HR3)。門は機能キー `hr.groups`。 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useApi } from "../../lib/useApi";

/**
 * 一覧の取得上限。**ページングは入れない**(Codex 低 2)。
 * 代わりに「表示 N 件 / 全 M 件」を画面に出して、黙って切れないようにする。
 */
export const LIST_LIMIT = 200;

/** 一覧の件数書き。打ち切っているときだけ全件数と上限を添える。 */
export function countNote(shown: number, total: number | undefined): string {
  const all = total ?? shown;
  return all > shown
    ? `表示 ${shown} 件 / 全 ${all} 件(${LIST_LIMIT} 件で打ち切り。条件で絞って見る)`
    : `全 ${all} 件`;
}

/** グループを触ると割当・タスク・監査が動くので、まとめて捨てる。 */
export function useInvalidateGroups() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["hr"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  }, [qc]);
}

/** クエリビルダーの選択肢。**画面に field を埋め込まない**(サーバーが正)。 */
export function useGroupFields() {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "group-fields"],
    queryFn: api.groupFields,
    staleTime: 30_000,
  });
}

export function useGroups(activeOnly = false) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "groups", activeOnly],
    queryFn: () =>
      api.groups(
        new URLSearchParams({
          limit: String(LIST_LIMIT),
          ...(activeOnly ? { active: "true" } : {}),
        }),
      ),
  });
}

export function useGroup(id: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "group", id],
    queryFn: () => api.group(id!),
    enabled: !!id,
  });
}

export function useGroupMembers(id: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "group-members", id],
    queryFn: () => api.groupMembers(id!, new URLSearchParams({ limit: String(LIST_LIMIT) })),
    enabled: !!id,
  });
}

export function useGroupEvents(id: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "group-events", id],
    queryFn: () => api.groupEvents(id!),
    enabled: !!id,
  });
}

export function usePermissionSets(groupId: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "permission-sets", groupId],
    queryFn: () => api.permissionSets(groupId!),
    enabled: !!groupId,
  });
}
